import { Box } from "@mui/material";
import { observer } from "mobx-react-lite";
import { Fragment, ReactElement } from "react";
import { BriaImageProps, ImageErrorType } from "../../../components/common/Galleries/BriaImage";
import { useAppStore } from "../../../hooks/useStores";
import StructuredPromptImageCanvas from "../../../layout/LabsLayout/Results/Canvas/StructuredPromptImageCanvas";
import LabImageResult from "../../../layout/LabsLayout/Results/LabImageResult/LabImageResult";
import { PlaygroundImage, PlaygroundResult } from "../../../models/image-to-image";
import { TextToImageConfigType } from "../../../models/text-to-image";
import { isFibo2App, isFiboBbqApp } from "../../../utils";
import PlaygroundImageOverlay from "../../Playground/Images/Overlay/PlaygroundImageOverlay";
import PromptTextWithActions from "../../Playground/TextToImage/Generator/PromptTextWithActions/PromptTextWithActions";
import styles from "./FiboGenerateResult.module.scss";

export interface FiboGenerateResultProps {
	playgroundResult: PlaygroundResult;
	extraResultInfo: ReactElement | null;
	resultHeaderActions: ReactElement | null;
}

const FiboGenerateResult = ({ playgroundResult, extraResultInfo, resultHeaderActions }: FiboGenerateResultProps) => {
	const { playgroundStore, textToImageStore, fiboStore } = useAppStore();
	const config = playgroundResult.config as TextToImageConfigType;
	const isRefineTab = fiboStore.activeTab === "refine";

	const handleSuccessPulling = (image: PlaygroundImage, result?: any) => {
		if (result) {
			image.url = result.image_url;
			image.seed = result.seed;
			image.warning = result.warning;
			image.loading = false;

			const group = playgroundStore.labResultGroups.find((g) => g.id === playgroundResult.id);
			if (playgroundStore.isFiboResultGroup(group) && result.structured_prompt) {
				const updates: any = {
					is_structured_prompt_loading: false,
				};
				// // When API response comes, use it as the authoritative source of truth
				// // Update both structured_prompt and original_structured_prompt to match the API response
				// // This ensures they're synchronized and prevents false positives in modification detection
				// if (!group.original_structured_prompt) {
				// 	// Set both to API value if original doesn't exist yet
				// 	updates.structured_prompt = result.structured_prompt;
				// 	updates.original_structured_prompt = result.structured_prompt;
				// } else if (group.structured_prompt !== result.structured_prompt) {
				// 	// If original exists but structured_prompt differs from API, sync both to API value
				// 	// This handles the case where structured_prompt was set earlier with a different format
				// 	updates.structured_prompt = result.structured_prompt;
				// 	updates.original_structured_prompt = result.structured_prompt;
				// } else if (!group.structured_prompt) {
				// 	// If original exists but structured_prompt doesn't, set structured_prompt to match original
				// 	updates.structured_prompt = group.original_structured_prompt;
				// }
				fiboStore.updateFiboResultGroup(playgroundResult.id, updates);
			}

			const currentGroup = fiboStore.getSelectedFiboGroup();
			if (currentGroup?.id === playgroundResult.id) {
				const isCurrentlySelectedImage =
					currentGroup.selectedImage &&
					"id" in currentGroup.selectedImage &&
					currentGroup.selectedImage.id === image.id;

				if (isCurrentlySelectedImage) {
					fiboStore.updateFiboResultGroup(playgroundResult.id, {
						selectedImage: image,
						is_structured_prompt_loading: false,
					});
				}
			}

			const warningMessage = result?.warning?.toLowerCase();
			if (
				warningMessage &&
				(warningMessage.includes("intellectual property") || warningMessage.includes("ip-protected"))
			) {
				textToImageStore.errors.generate = {
					message: "promptIpWarning",
					status: 200,
					isWarning: true,
				};
			}
		}
		playgroundStore.onSuccessResult(image);
	};

	const handleSelectedImageChange = (image: PlaygroundImage) => {
		fiboStore.updateFiboResultGroup(playgroundResult.id, {
			selectedImage: image,
		});
	};

	const handleErrorPulling = (image: PlaygroundImage, errorType: ImageErrorType) => {
		playgroundStore.onErrorResult(image, errorType);

		const images = playgroundResult?.images?.filter(Boolean) ?? [];
		const allImagesFinished = images.length === 0 || images.every((img) => img && !img.loading);
		if (allImagesFinished) {
			const group = playgroundStore.labResultGroups.find((g) => g.id === playgroundResult.id);
			if (group && playgroundStore.isFiboResultGroup(group) && group.is_structured_prompt_loading) {
				fiboStore.updateFiboResultGroup(playgroundResult.id, {
					is_structured_prompt_loading: false,
				});
			}
		}
	};

	const renderImageOverlay = (image: PlaygroundImage) => <PlaygroundImageOverlay image={image} hideAiEditorButton />;

	const renderImageExtra = (image: PlaygroundImage, imageElement: HTMLImageElement | null) => {
		const isFiboPro = (image.config as TextToImageConfigType)?.model === "fibo";
		const isFiboLite = (image.config as TextToImageConfigType)?.model_id === "fibo_lite";
		const group = playgroundStore.labResultGroups.find((g) => g.id === playgroundResult.id);

		if (
			isRefineTab &&
			(isFiboBbqApp() || isFibo2App()) &&
			isFiboPro &&
			!isFiboLite &&
			playgroundStore.isFiboResultGroup(group) &&
			group.structured_prompt
		) {
			return <StructuredPromptImageCanvas image={image} imageElement={imageElement} />;
		}
		return null;
	};

	const isCanvasHovered = fiboStore.structuredPromptStore.isCanvasHovered;
	const isEmbedDrawActive = fiboStore.structuredPromptStore.isEmbedDrawActive;

	const getImageProps = (image: PlaygroundImage): Partial<BriaImageProps> => ({
		fullScreenProps: {
			fileName: playgroundStore.getFileName(image),
			format: (image.config as TextToImageConfigType)?.format,
		},
		hideApiReferenceButton: false,
		downloadProps: { instantDownload: true },
		// In BBQ / FIBO 2 refine mode: disable image click and fix overlay z-index for canvas interaction
		...(isRefineTab &&
			(isFiboBbqApp() || isFibo2App()) && {
				handleClick: undefined,
				wrapperClassName: styles.fiboImageWrapper,
				showConstantOverlay: isCanvasHovered,
			}),
		// Drawing an embed area wants a clean image: the hover overlay buttons (download /
		// fullscreen / feedback) sit on the pixels the user is dragging across.
		...(isEmbedDrawActive && {
			displayOverlay: "off" as const,
			showConstantOverlay: false,
			handleClick: undefined,
		}),
	});

	return (
		<LabImageResult
			playgroundResult={playgroundResult}
			headerContent={
				<Box className={styles.headerContent}>
					{extraResultInfo && <Fragment>{extraResultInfo}</Fragment>}
					<PromptTextWithActions
						prompt={config?.prompt}
						promptClassName={styles.prompt}
						resultHeaderActions={resultHeaderActions}
					/>
				</Box>
			}
			renderImageOverlay={renderImageOverlay}
			renderImageExtra={renderImageExtra}
			getImageProps={getImageProps}
			fullScreenProps={{
				hideApiReferenceButton: false,
				hideFeedbackButtons: false,
			}}
			onSuccessPulling={handleSuccessPulling}
			onErrorPulling={handleErrorPulling}
			onSelectedImageChange={handleSelectedImageChange}
			abortController={fiboStore.abortController || undefined}
			className={styles.fiboGenerateResult}
			anonymousFeature={
				isRefineTab
					? isFiboBbqApp()
						? "labs_bbq_refine"
						: isFibo2App()
							? "labs_fibo2_refine"
							: "labs_refine"
					: isFiboBbqApp()
						? "labs_bbq_generate"
						: isFibo2App()
							? "labs_fibo2_generate"
							: "labs_generate"
			}
		/>
	);
};

export default observer(FiboGenerateResult);
